import DOMPurify from "dompurify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { AlertTriangle, CheckCircle2, ListChecks } from "lucide-react";
import type { Evaluation, CritereEvaluation } from "../AOWizard";

interface EvaluationsSummaryCardProps {
  evaluations: Evaluation[];
  criteresMap: Record<number, CritereEvaluation[]>;
}

export function EvaluationsSummaryCard({ evaluations, criteresMap }: EvaluationsSummaryCardProps) {
  const calculateCriteresTotalPct = (evaluationIndex: number): number => {
    const criteres = criteresMap[evaluationIndex] || [];
    return criteres.reduce((sum, critere) => sum + (critere.ponderation_pct || 0), 0);
  };

  const getPonderationBadge = (total: number) => {
    const isValid = total === 100;
    return (
      <Badge 
        variant={isValid ? "default" : "destructive"}
        className={`text-xs ${isValid ? 'bg-green-600 hover:bg-green-700' : ''}`}
      >
        {isValid ? (
          <>
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {total}%
          </>
        ) : (
          <>
            <AlertTriangle className="h-3 w-3 mr-1" />
            {total}% (attendu: 100%)
          </>
        )}
      </Badge>
    );
  };

  if (evaluations.length === 0) {
    return (
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="flex items-center">
            <ListChecks className="h-5 w-5 mr-2" />
            Résumé des évaluations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Aucune évaluation définie. Retournez à l'étape précédente pour ajouter des évaluations.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-shadow">
      <CardHeader>
        <CardTitle className="flex items-center">
          <ListChecks className="h-5 w-5 mr-2" />
          Résumé des évaluations et critères
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {evaluations.map((evaluation, index) => {
          const criteres = criteresMap[index] || [];
          const totalPct = calculateCriteresTotalPct(index);
          const isValid = totalPct === 100;

          return (
            <div 
              key={index} 
              className={`p-4 rounded-lg border ${isValid ? 'bg-card' : 'bg-destructive/5 border-destructive/20'}`}
            >
              {/* En-tête de l'évaluation */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-base">{evaluation.nom}</h4>
                    <Badge variant="outline" className="text-xs">
                      Pondération: {evaluation.ponderation_globale_pct}%
                    </Badge>
                  </div>
                  {evaluation.description && (
                    <p className="text-sm text-muted-foreground">{evaluation.description}</p>
                  )}
                </div>
              </div>

              {/* Liste des critères */}
              {criteres.length > 0 ? (
                <div className="space-y-3 mt-4">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <Label className="text-xs text-muted-foreground">
                      CRITÈRES ({criteres.length})
                    </Label>
                    {getPonderationBadge(totalPct)}
                  </div>
                  
                  {criteres.map((critere, critereIndex) => (
                    <div 
                      key={critereIndex}
                      className="pl-4 border-l-2 border-muted"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{critere.nom}</span>
                            <Badge variant="secondary" className="text-xs">
                              {critere.ponderation_pct}%
                            </Badge>
                          </div>
                          {critere.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {critere.description}
                            </p>
                          )}
                          {critere.contenu_html && (
                            <div
                              className="text-xs text-muted-foreground mt-2 prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(critere.contenu_html) }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 p-3 bg-muted/50 rounded-md">
                  <p className="text-sm text-muted-foreground flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Aucun critère défini pour cette évaluation
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
